/**
 * Product Form Component
*/
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer');
        this.submitButtons = this.querySelectorAll('[data-add-to-cart]');
        this.quantityInput = this.querySelector('[data-quantity-input]');
        this.isSubmitting = false;

        // Initialize sticky button handler only once globally
        if (!window.__productFormStickyInitialized) {
          window.__productFormStickyInitialized = true;
          document.addEventListener('click', (e) => {
            const stickyBtn = e.target.closest('[data-add-to-cart-sticky]');
            if (stickyBtn && !stickyBtn.disabled && stickyBtn.getAttribute('aria-disabled') !== 'true') {
              e.preventDefault();
              e.stopPropagation();

              // Find the product-form in the same section/container as the sticky button
              // Look for closest .hero or section, then find product-form within it
              const heroSection = stickyBtn.closest('.hero, section');
              let productForm = heroSection ? heroSection.querySelector('product-form') : null;

              // If not found in section, try to find any product-form on the page
              if (!productForm) {
                productForm = document.querySelector('product-form');
              }

              if (productForm) {
                // Create a synthetic submit event from the form
                const form = productForm.querySelector('form');
                if (form) {
                  const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                  form.dispatchEvent(submitEvent);
                }
              }
            }
          }, true); // Use capture phase to catch event before other handlers
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        
        const submitButton = evt.submitter || this.submitButtons[0];
        if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.isSubmitting) return;

        this.isSubmitting = true;

        try {
          if (
            window.YasminaLitePreorderModal &&
            typeof window.YasminaLitePreorderModal.confirmIfNeeded === 'function'
          ) {
            const shouldProceed = await window.YasminaLitePreorderModal.confirmIfNeeded({
              form: this.form,
              submitButton,
            });
            if (!shouldProceed) {
              this.isSubmitting = false;
              return;
            }
          }
        } catch (error) {
          console.error('Lite preorder modal error:', error);
        }

        const cartDrawer = this.cart || document.querySelector('cart-drawer');
        this.cart = cartDrawer;

        const allButtons = [
          ...this.submitButtons, 
          ...document.querySelectorAll('[data-add-to-cart-sticky]')
        ];
        
        allButtons.forEach(btn => {
          btn.setAttribute('aria-disabled', 'true');
          btn.disabled = true;

          const label = btn.querySelector('.home-hero__button-label, .header-speaker-card__button-label');
          const defaultFallback = btn.hasAttribute('data-add-to-cart-sticky') ? 'Add' : 'Add to Cart';
          const fallbackOriginal = btn.dataset.currentText || defaultFallback;
          const labelText = label ? label.textContent.trim() : '';
          btn.dataset.originalText = labelText || fallbackOriginal;

          if (label) {
            label.textContent = 'Adding...';
          } else {
            btn.textContent = 'Adding...';
          }
        });

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        
        if (cartDrawer) {
          formData.append('sections', cartDrawer.getSectionsToRender().map((section) => section.id).join(','));
          formData.append('sections_url', window.location.pathname);
        }
        
        config.body = formData;

        try {
          let response = await fetch(window.routes.cart_add_url, config).then((res) => res.json());
          const errMsg = response.status ? response.description || response.message || '' : '';
          const fallbackVariantId = this.getFallbackVariantId(formData.get('id'));

          if (response.status && this.isSoftStockError(errMsg) && !this.quantityInput && fallbackVariantId) {
            formData.set('id', fallbackVariantId);
            config.body = formData;
            response = await fetch(window.routes.cart_add_url, config).then((res) => res.json());
          }

          if (response.status) {
            const finalErrMsg = response.description || response.message || '';

            if (this.isSoftStockError(finalErrMsg) && this.quantityInput) {
              try {
                const isLiteTemplate =
                  document.body?.classList.contains('product-template-lite') ||
                  document.body?.classList.contains('product-template-lite-plus') ||
                  document.body?.classList.contains('product-template-lite-plus-2');
                if (typeof updateStockStatusDisplay === 'function') {
                  updateStockStatusDisplay(false, 0);
                }
                if (!isLiteTemplate && typeof setAddToCartEnabled === 'function') {
                  setAddToCartEnabled(false);
                }
                const current = parseInt(this.quantityInput.value) || 1;
                this.quantityInput.setAttribute('max', current);
                this.quantityInput.setAttribute('data-max', current);
              } catch (e) {}
            } else {
              this.handleErrorMessage(finalErrMsg);
            }

            this.resetButtons();

            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
            });

            return;
          }

          if (!cartDrawer) {
            this.resetButtons();
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          });

          cartDrawer.renderContents(response);

          if (this.quantityInput) {
            this.quantityInput.value = 1;
          }

          this.resetButtons();

          const cartUpdatedEvent = new CustomEvent('cart:updated', { detail: response });
          document.dispatchEvent(cartUpdatedEvent);
        } catch (error) {
          console.error('Error adding to cart:', error);
          this.handleErrorMessage('Failed to add item to cart. Please try again.');
          this.resetButtons();
        } finally {
          this.isSubmitting = false;
        }
      }

      resetButtons() {
        const allButtons = [
          ...this.submitButtons, 
          ...document.querySelectorAll('[data-add-to-cart-sticky]')
        ];
        
        allButtons.forEach(btn => {
          btn.removeAttribute('aria-disabled');
          btn.disabled = false;

          const label = btn.querySelector('.home-hero__button-label, .header-speaker-card__button-label');
          const defaultFallback = btn.hasAttribute('data-add-to-cart-sticky') ? 'Add' : 'Add to Cart';
          const fallbackOriginal = btn.dataset.currentText || defaultFallback;
          const storedText = btn.dataset.originalText || '';
          const textToRestore = storedText || fallbackOriginal;

          if (label) {
            label.textContent = textToRestore;
          } else {
            btn.textContent = textToRestore;
          }
        });
      }

      handleErrorMessage(errorMessage = false) {
        console.error('Cart Error:', errorMessage);
        if (errorMessage) {
          if (window.NotificationManager) {
            window.NotificationManager.error(errorMessage);
          } else {
            alert(errorMessage);
          }
        }
      }

      isSoftStockError(message) {
        if (!message) return false;
        const text = String(message).toLowerCase();
        return (
          text.includes('maximum quantity') ||
          text.includes('already in your cart') ||
          text.includes('out of stock') ||
          text.includes('sold out')
        );
      }

      getFallbackVariantId(currentVariantId) {
        const fallbackVariantId = this.form?.dataset?.fallbackVariantId;
        if (!fallbackVariantId || fallbackVariantId === String(currentVariantId)) return '';
        return fallbackVariantId;
      }
    }
  );
}
